/* src/components/customGame/setup/panels/stimuli/PositionStimulusPanel.js */
import React, { useState, useEffect } from 'react'; // Added useState, useEffect for local state if needed
import PropTypes from 'prop-types';
// We might need a visual grid editor component later
// import GridEditor from '../../controls/GridEditor';

// Re-using the SubSettingsSection helper for consistency
const SubSettingsSection = ({ title, children, helpText = "" }) => (
    <div className="mb-4 pt-3">
        <label className="block text-sm font-semibold text-gray-700 mb-1">{title}</label>
        {helpText && <p className="text-xs text-gray-500 mb-2">{helpText}</p>}
        <div className="space-y-3">
            {children}
        </div>
    </div>
);

SubSettingsSection.propTypes = {
    title: PropTypes.string.isRequired,
    children: PropTypes.node.isRequired,
    helpText: PropTypes.string,
};


const PositionStimulusPanel = ({ config, onConfigChange, pathPrefix, globalStimulusConfig }) => {
    const currentConfig = config || {};

    const handleChange = (key, value) => {
        onConfigChange(`${pathPrefix}.${key}`, value);
    };

    // Handler for when GridSize dropdown changes, to also update GridWidth/Height if not "Custom"
    const handleGridSizeChange = (newGridSize) => {
        handleChange('GridSize', newGridSize);
        if (newGridSize !== 'Custom') {
            const dims = newGridSize.split('x');
            if (dims.length === 2) {
                const width = parseInt(dims[0], 10);
                const height = parseInt(dims[1], 10);
                handleChange('GridWidth', width);
                handleChange('GridHeight', height);
            }
        }
    };

    // For AllowedLocations, a simple text display for now. Will be a GridEditor later.
    const displayAllowedLocations = () => {
        if (Array.isArray(currentConfig.AllowedLocations) && currentConfig.AllowedLocations.length > 0) {
            // Assuming AllowedLocations stores coordinates like [{x:0, y:0}, {x:1, y:0}]
            // This is just a placeholder representation.
            return currentConfig.AllowedLocations.map(loc => `(${loc.x},${loc.y})`).join('; ');
        }
        const gridSize = currentConfig.GridSize || '3x3';
        if (gridSize === '3x3' && (!currentConfig.AllowedLocations || currentConfig.AllowedLocations.length === 0) ) {
             return "Default: All outer 8 cells of a 3x3 grid";
        }
        return "Not specified (uses defaults or requires custom input)";
    };


    return (
        <div className="space-y-4">
            <SubSettingsSection title="Grid Configuration" helpText="Define the spatial layout for position stimuli.">
                <div>
                    <label htmlFor={`${pathPrefix}-GridSize`} className="block text-xs font-medium text-gray-600 mb-1">
                        Grid Size
                    </label>
                    <select
                        id={`${pathPrefix}-GridSize`}
                        name="GridSize"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.GridSize || '3x3'}
                        onChange={(e) => handleGridSizeChange(e.target.value)}
                    >
                        <option value="3x3">3x3 (Standard)</option>
                        <option value="4x4">4x4</option>
                        <option value="5x5">5x5</option>
                        <option value="Custom">Custom Dimensions</option>
                    </select>
                </div>

                {currentConfig.GridSize === 'Custom' && (
                    <div className="grid grid-cols-2 gap-4">
                        <div>
                            <label htmlFor={`${pathPrefix}-GridWidth`} className="block text-xs font-medium text-gray-600 mb-1">
                                Grid Width (cells)
                            </label>
                            <input
                                type="number"
                                id={`${pathPrefix}-GridWidth`}
                                name="GridWidth"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={currentConfig.GridWidth || 3}
                                onChange={(e) => handleChange('GridWidth', parseInt(e.target.value, 10))}
                                min="2"
                                max="10"
                            />
                        </div>
                        <div>
                            <label htmlFor={`${pathPrefix}-GridHeight`} className="block text-xs font-medium text-gray-600 mb-1">
                                Grid Height (cells)
                            </label>
                            <input
                                type="number"
                                id={`${pathPrefix}-GridHeight`}
                                name="GridHeight"
                                className="mt-1 block w-full pl-3 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                                value={currentConfig.GridHeight || 3}
                                onChange={(e) => handleChange('GridHeight', parseInt(e.target.value, 10))}
                                min="2"
                                max="10"
                            />
                        </div>
                    </div>
                )}

                <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">
                        Allowed Stimulus Locations
                    </label>
                    {/* Placeholder for GridEditor - for now, just display or allow text input */}
                    <div className="mt-1 p-2 border border-gray-300 rounded-md bg-gray-50 text-sm text-gray-700 min-h-[40px]">
                        {displayAllowedLocations()}
                    </div>
                    <p className="text-xs text-gray-500 mt-1">
                        A visual grid editor for selecting allowed cells will be available later.
                        The default for 3x3 is typically the 8 outer cells.
                    </p>
                    {/* <GridEditor 
                        width={currentConfig.GridWidth || 3} 
                        height={currentConfig.GridHeight || 3}
                        allowedLocations={currentConfig.AllowedLocations || []}
                        onChange={(newLocations) => handleChange('AllowedLocations', newLocations)}
                    /> */}
                </div>
            </SubSettingsSection>

            <SubSettingsSection title="Stimulus Marker Appearance" helpText="Customize the visual marker.">
                <div>
                    <label htmlFor={`${pathPrefix}-StimulusShape`} className="block text-xs font-medium text-gray-600 mb-1">
                        Marker Shape
                    </label>
                    <select
                        id={`${pathPrefix}-StimulusShape`}
                        name="StimulusShape"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.StimulusShape || 'Square'}
                        onChange={(e) => handleChange('StimulusShape', e.target.value)}
                    >
                        <option value="Square">Square</option>
                        <option value="Circle">Circle</option>
                        {/* <option value="Custom Image">Custom Image (Advanced)</option> */}
                    </select>
                </div>
                 <div>
                    <label htmlFor={`${pathPrefix}-StimulusColor`} className="block text-xs font-medium text-gray-600 mb-1">
                        Marker Color
                    </label>
                    <input
                        type="color"
                        id={`${pathPrefix}-StimulusColor`}
                        name="StimulusColor"
                        className="mt-1 block w-full h-10 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={currentConfig.StimulusColor || '#3B82F6'} // Default blue
                        onChange={(e) => handleChange('StimulusColor', e.target.value)}
                    />
                     {globalStimulusConfig?.EnabledTypes?.includes('Color') && (
                        <p className="mt-1 text-xs text-yellow-600 bg-yellow-50 p-2 rounded-md">
                            Warning: The "Color" stimulus modality is also active. Ensure this marker color does not conflict with your Color stimulus palette, or manage this interaction intentionally.
                        </p>
                    )}
                </div>
            </SubSettingsSection>

            <SubSettingsSection title="Grid Visual Style" helpText="Customize the grid background and lines.">
                <div>
                    <label htmlFor={`${pathPrefix}-GridAppearance`} className="block text-xs font-medium text-gray-600 mb-1">
                        Grid Lines / Guides
                    </label>
                    <select
                        id={`${pathPrefix}-GridAppearance`}
                        name="GridAppearance"
                        className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm rounded-md"
                        value={currentConfig.GridAppearance || 'Visible Borders'}
                        onChange={(e) => handleChange('GridAppearance', e.target.value)}
                    >
                        <option value="Visible Borders">Visible Borders</option>
                        <option value="Minimal Dots">Minimal Dots (at intersections)</option>
                        <option value="Hidden Borders">Hidden Borders (No guides)</option>
                    </select>
                </div>
                <div>
                    <label htmlFor={`${pathPrefix}-BackgroundColor`} className="block text-xs font-medium text-gray-600 mb-1">
                        Grid Area Background Color
                    </label>
                    <input
                        type="color"
                        id={`${pathPrefix}-BackgroundColor`}
                        name="BackgroundColor"
                        className="mt-1 block w-full h-10 border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
                        value={currentConfig.BackgroundColor || '#F3F4F6'} // Default light gray
                        onChange={(e) => handleChange('BackgroundColor', e.target.value)}
                    />
                </div>
            </SubSettingsSection>

            {/* Debug section (optional) */}
            {/* <details className="mt-4 text-xs">
                <summary className="cursor-pointer text-gray-500">Show Position Panel Debug Info</summary>
                <pre className="mt-2 p-2 bg-gray-100 rounded text-gray-700 overflow-auto">
                    Path Prefix: {pathPrefix}{"\n"}
                    Config: {JSON.stringify(currentConfig, null, 2)}
                </pre>
            </details> */}
        </div>
    );
};

PositionStimulusPanel.propTypes = {
    config: PropTypes.shape({
        GridSize: PropTypes.string,
        GridWidth: PropTypes.number,
        GridHeight: PropTypes.number,
        AllowedLocations: PropTypes.array, // Will be array of objects {x, y}
        StimulusShape: PropTypes.string,
        StimulusColor: PropTypes.string,
        GridAppearance: PropTypes.string,
        BackgroundColor: PropTypes.string,
    }),
    onConfigChange: PropTypes.func.isRequired,
    pathPrefix: PropTypes.string.isRequired, // e.g., "Stimulus.Position"
    globalStimulusConfig: PropTypes.object, // The entire Stimulus settings object
};

export default PositionStimulusPanel;